// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import constants from "@store/constants";
import ProductLine, { ProductLinePresentationProps } from "@store/Library/Components/ProductLine/ProductLine";
import { ProductLineStatusType } from "@store/Library/Components/ProductLine/ProductLineStatusOverlay";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import React, { FC, useContext } from "react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import OrderChangeRequestLineForm, {
    orderChangeRequestLineCanceledState,
    orderChangeRequestLineRestoredState,
} from "@store/Library/Components/OrderChangeRequest/OrderChangeRequestLineForm";
import getOrderChangeRequestProductLineStatus from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestProductLineStatus";
import getOrderLineHasStartedShipping from "@store/Framework/Common/Utilities/getOrderLineHasStartedShipping";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/UpdateChangeRequestOrder";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { css } from "styled-components";
import { productLineStatusAboveOverlayZIndex } from "@store/Library/Components/ProductLine/ProductLineStatusOverlay";
import CloseCircleLine from "@store/Library/Components/Toolkit/CloseCircle/CloseCircleLine";
import ArrowRoundLeft from "@store/Library/Components/Icons/ArrowRoundLeft";
import { OrderChangeRequestLineStateModel } from "@store/Framework/Types/ApiModels";
import siteMessage from "@insite/client-framework/SiteMessage";

export interface OrderChangeRequestProductListProps extends WidgetProps {}

export interface OrderChangeRequestProductListStyles {
    cancelLink?: LinkPresentationProps;
    productLine?: ProductLinePresentationProps;
    removeLink?: LinkPresentationProps;
    restoreLink?: LinkPresentationProps;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

const sharedActionLinkProps: LinkPresentationProps = {
    css: css`
        z-index: ${productLineStatusAboveOverlayZIndex};
    `,
};

export const orderChangeRequestProductListStyles: OrderChangeRequestProductListStyles = {
    wrapper: orderV2Props.sectionWrapper,
    title: orderV2Props.sectionTitle,
    productLine: orderV2Props.productLine,
    cancelLink: mergeProps(linkGetActionLinkProps(CloseCircleLine), sharedActionLinkProps),
    removeLink: mergeProps(linkGetActionLinkProps(CloseCircleLine), sharedActionLinkProps),
    restoreLink: mergeProps(linkGetActionLinkProps(ArrowRoundLeft), sharedActionLinkProps),
};

const styles = orderChangeRequestProductListStyles;

const OrderChangeRequestProductList: FC<OrderChangeRequestProductListProps> = () => {
    const { value: order } = useContext(OrderStateContext);
    const dispatch = useAppDispatch();
    const orderChangeRequest = useAppSelector(state => state.greif.pages.orderChange.changeRequestOrder);

    if (!order) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Items in your order")}</Typography>
            {order.orderLines?.map(orderLine => {
                const orderChangeRequestLine = orderChangeRequest?.orderLines?.find(
                    x => x.orderLineId === orderLine.id,
                );
                const status = getOrderChangeRequestProductLineStatus(orderChangeRequestLine);
                const hasStartedShipping = getOrderLineHasStartedShipping(order, orderLine.id);
                const isEditable = !!orderChangeRequestLine && !hasStartedShipping;
                const isCanceled = status === "canceled";

                const updateLineState = (update: Partial<OrderChangeRequestLineStateModel>) => {
                    const orderLines = orderChangeRequest.orderLines.map(x =>
                        x.orderLineId === orderLine.id ? { ...x, ...update } : x,
                    );

                    dispatch(updateChangeRequestOrder({ orderLines }));
                };

                return (
                    <ProductLine
                        {...styles.productLine}
                        actionLink={
                            isEditable ? (
                                <DisablerContext.Provider value={{ disable: false }}>
                                    <Link
                                        {...(isCanceled ? styles.restoreLink : styles.cancelLink)}
                                        data-test-selector="OrderChangeRequest_cancelRestoreBtn"
                                        onClick={() => {
                                            updateLineState(
                                                isCanceled
                                                    ? orderChangeRequestLineRestoredState
                                                    : orderChangeRequestLineCanceledState,
                                            );
                                        }}
                                    >
                                        {isCanceled ? translate("Restore item") : translate("Cancel item")}
                                    </Link>
                                </DisablerContext.Provider>
                            ) : undefined
                        }
                        additionalAlternativeContent={
                            isEditable ? (
                                orderLine.productId ? (
                                    <OrderChangeRequestLineForm
                                        lineState={orderChangeRequestLine}
                                        onUpdate={updateLineState}
                                        status={status}
                                    />
                                ) : (
                                    siteMessage("OrderChange_ProductNoLongerAvailable")
                                )
                            ) : undefined
                        }
                        key={orderLine.id}
                        productLine={orderLine}
                        showPrice
                        showSubtotal
                        showLineNumber
                        showFulfillment
                        status={status}
                    />
                );
            })}
            {orderChangeRequest?.newLines?.map(newLine => {
                const status: ProductLineStatusType = "added";

                const updateLineState = (update: Partial<OrderChangeRequestLineStateModel>) => {
                    const newLines = orderChangeRequest.newLines?.map(x =>
                        x.product.id === newLine.product.id ? { ...x, ...update } : x,
                    );

                    dispatch(updateChangeRequestOrder({ newLines }));
                };

                const removeLine = () => {
                    const newLines = orderChangeRequest.newLines?.filter(x => x.product.id !== newLine.product.id);

                    dispatch(updateChangeRequestOrder({ newLines }));
                };

                return (
                    <ProductLine
                        {...styles.productLine}
                        actionLink={
                            <DisablerContext.Provider value={{ disable: false }}>
                                <Link
                                    {...styles.removeLink}
                                    data-test-selector="OrderChangeRequest_removeBtn"
                                    onClick={removeLine}
                                >
                                    {translate("Cancel item")}
                                </Link>
                            </DisablerContext.Provider>
                        }
                        additionalAlternativeContent={
                            <OrderChangeRequestLineForm
                                lineState={newLine}
                                onUpdate={updateLineState}
                                status={status}
                            />
                        }
                        key={newLine.product.id}
                        productLine={{
                            ...newLine.product,
                            pricing: newLine.productInfo.pricing ?? null,
                            qtyOrdered: newLine.newQuantity === "" ? null : Number(newLine.newQuantity),
                        }}
                        productInfo={newLine.productInfo}
                        showLineNumber
                        showFulfillment
                        showQuantity={false}
                        status={status}
                    />
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderChangeRequestProductList,
    definition: {
        group: constants.widgetGroup.orderChangeRequest,
        displayName: "Order Change Request Product List",
        allowedContexts: [constants.pageType.orderChangeRequest],
    },
};

export default widgetModule;
